import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { GrClose } from "react-icons/gr";
import Spinner from "components/spinner";
import styles from "./HeritageItemPurchaseHistoryModal.module.scss";

export type HeritagePurchaseHistoryEntry = {
  orderDate: string | null;
  orderId: string | null;
  heritageOrderId?: string | null;
  quantity: number | string | null;
  unitOfMeasure?: string | null;
  status?: string | null;
  source: "heritage" | "eserv" | string;
};

export type HeritagePurchaseHistoryResponse = {
  heritagePartNumber: string;
  summary: {
    timesOrdered: number | null;
    totalQuantity: number | null;
    lastOrderedDate: string | null;
  } | null;
  entries: HeritagePurchaseHistoryEntry[];
};

type Props = {
  heritagePartNumber: string;
  description?: string | null;
  vendorId?: number | null;
  onClose: () => void;
};

const HeritageItemPurchaseHistoryModal = ({
  heritagePartNumber,
  description,
  vendorId,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const tBase = "views.vendors.VendorBody.VendorItems.VendorItemLinkModal.VendorItemLinkModalBody";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<HeritagePurchaseHistoryResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    const params: Record<string, number> = {};
    if (vendorId) params.vendorId = vendorId;

    setLoading(true);
    setError(null);
    axios
      .get<HeritagePurchaseHistoryResponse>(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/items/${encodeURIComponent(
          heritagePartNumber
        )}/purchase-history`,
        { params }
      )
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setData(null);
        setError(err?.response?.data?.message || tr("Purchase history load failed"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [heritagePartNumber, vendorId, t]);

  const formatDate = (value: string | null | undefined) => {
    if (!value) return "—";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
  };

  const modal = (
    <div className={styles.overlay} role="presentation" onClick={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="heritage-purchase-history-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <div className="min-w-0 pr-3">
            <h2 id="heritage-purchase-history-title" className={styles.title}>
              {tr("Purchase history")}
            </h2>
            <p className={styles.subtitle}>{heritagePartNumber}</p>
            {description && <p className={styles.description}>{description}</p>}
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={tr("Close")}
          >
            <GrClose size={18} />
          </button>
        </div>

        <div className={styles.body}>
          {loading ? (
            <div className={styles.centered}>
              <Spinner />
            </div>
          ) : error ? (
            <p className={styles.error} role="alert">
              {error}
            </p>
          ) : (
            <>
              {data?.summary && (
                <div className={styles.summaryGrid}>
                  <div className={styles.summaryCard}>
                    <span className={styles.summaryLabel}>{tr("Times ordered")}</span>
                    <span className={styles.summaryValue}>
                      {data.summary.timesOrdered ?? "—"}
                    </span>
                  </div>
                  <div className={styles.summaryCard}>
                    <span className={styles.summaryLabel}>{tr("Total quantity")}</span>
                    <span className={styles.summaryValue}>
                      {data.summary.totalQuantity ?? "—"}
                    </span>
                  </div>
                  <div className={styles.summaryCard}>
                    <span className={styles.summaryLabel}>{tr("Last ordered")}</span>
                    <span className={styles.summaryValue}>
                      {formatDate(data.summary.lastOrderedDate)}
                    </span>
                  </div>
                </div>
              )}

              {!data?.entries?.length ? (
                <p className={styles.empty}>{tr("No purchase history found for this item")}</p>
              ) : (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{tr("Date")}</th>
                        <th>{tr("Order")}</th>
                        <th>{tr("Qty")}</th>
                        <th>{tr("Source")}</th>
                        <th>{tr("Status")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.entries.map((entry, index) => (
                        <tr key={`${entry.source}-${entry.orderId}-${entry.orderDate}-${index}`}>
                          <td>{formatDate(entry.orderDate)}</td>
                          <td>
                            {entry.orderId || entry.heritageOrderId || "—"}
                          </td>
                          <td>
                            {entry.quantity ?? "—"}
                            {entry.unitOfMeasure ? ` ${entry.unitOfMeasure}` : ""}
                          </td>
                          <td>
                            {entry.source === "heritage"
                              ? tr("Heritage")
                              : entry.source === "eserv"
                                ? tr("EServ")
                                : entry.source}
                          </td>
                          <td>{entry.status || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default HeritageItemPurchaseHistoryModal;
